import React from 'react';
import PartnerPageTemplate from './PartnerPageTemplate';
import {
  Hospital,
  Building2,
  Users,
  Clock,
  ShieldCheck,
  FileCheck,
  MapPin,
  Phone,
  HeartPulse,
  ClipboardList,
} from 'lucide-react';

const CANONICAL = 'https://www.moveracaremobility.com/healthcare-partners/hospital-discharge';

const HospitalDischargePartners = () => {
  const seo = {
    title: 'Hospital Discharge Transportation Partner | Greater Houston | MoveraCare',
    description: 'Same-day and scheduled hospital discharge non-emergency medical transportation for hospital discharge teams, case managers, and utilization review across Greater Houston.',
    canonical: CANONICAL,
    schemaId: 'partner-hospital-discharge-schema',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Hospital Discharge NEMT Coordination',
      serviceType: 'Non-Emergency Medical Transportation',
      areaServed: 'Greater Houston, TX',
      provider: {
        '@type': 'MedicalBusiness',
        name: 'MoveraCare Mobility',
        telephone: '+1-832-925-2128',
        url: 'https://www.moveracaremobility.com',
      },
      audience: {
        '@type': 'MedicalAudience',
        audienceType: 'Hospital Discharge Teams, Case Managers, Utilization Review',
      },
    },
  };

  return (
    <PartnerPageTemplate
      testid="partner-hospital-discharge"
      seo={seo}
      eyebrow="HOSPITAL DISCHARGE TEAMS"
      heading="Discharge Transportation That Keeps Beds Moving"
      intro="MoveraCare Mobility supports hospital discharge planners, case managers, and utilization review teams with same-day and scheduled non-emergency medical transportation from hospital to home, SNF, rehab, or assisted living."
      audience={{
        title: 'For Hospital Discharge Planners & Case Management Teams',
        description: 'This page is built for hospital-based teams whose job is to move medically stable patients out of the hospital quickly, safely, and to the correct next site of care.',
        bullets: [
          'Hospital discharge planners and social workers',
          'Inpatient case management and utilization review',
          'Care transitions and readmission-reduction teams',
          'Emergency department social work and discharge coordination',
          'Bed management and patient flow teams',
          'Post-acute care coordinators and liaisons',
        ],
      }}
      problem={{
        title: 'When the Patient Is Ready, Transportation Should Be Too',
        description: 'Discharge delays inflate length of stay, block beds, and create clinical risk. MoveraCare provides responsive discharge transportation coordinated around real hospital workflows, not the other way around.',
        bullets: [
          'Same-day discharge transportation from hospital to home, SNF, rehab, or assisted living',
          'Wheelchair and ambulatory options; door-to-door or curb-to-curb assistance coordinated at booking',
          'Coordination with nursing station handoffs, discharge paperwork, and family',
          'Support for scheduled discharges when timing needs to be exact',
          'Recurring follow-up transportation to reduce readmissions',
        ],
      }}
      capabilities={{
        title: 'Discharge-Focused Transportation Capabilities',
        items: [
          { icon: Hospital, title: 'Same-Day Discharge Support', desc: 'Responsive dispatch capacity for unplanned same-day discharges within our service area.' },
          { icon: Clock, title: 'Scheduled Discharge Windows', desc: 'Structured pickup windows aligned to physician sign-off and paperwork completion timelines.' },
          { icon: Building2, title: 'Home / SNF / Rehab Delivery', desc: 'Delivery to home, skilled nursing, rehabilitation, or assisted living. Door-to-door or curb-to-curb assistance is coordinated at booking.' },
          { icon: HeartPulse, title: 'Wheelchair & Ambulatory', desc: 'Wheelchair-accessible vans and comfortable ambulatory options based on patient mobility.' },
          { icon: ClipboardList, title: 'Coordinated Handoff', desc: 'Driver handoff at nursing station or coordinator with attention to discharge paperwork and belongings.' },
          { icon: ShieldCheck, title: 'HIPAA-Conscious Operations', desc: 'Professional handling of patient information from dispatch through delivery.' },
        ],
      }}
      coordination={{
        title: 'How Discharge Coordination Works',
        steps: [
          { title: 'Discharge Trigger', desc: 'Case manager, discharge planner, or social work team contacts dispatch with patient and destination details.' },
          { title: 'Trip Confirmation', desc: 'Vehicle type assigned, pickup window confirmed, and any special assistance captured.' },
          { title: 'Bedside Pickup', desc: 'Driver checks in at nursing station, supports safe transfer, and confirms paperwork/belongings.' },
          { title: 'Delivery & Handoff', desc: 'Patient delivered to home, SNF, rehab, or assisted living. Door-to-door or curb-to-curb assistance is coordinated at booking.' },
        ],
      }}
      why={{
        title: 'Why Hospital Discharge Teams Rely on MoveraCare',
        items: [
          { icon: Hospital, title: 'Discharge-Ready Dispatch', desc: 'Same-day and scheduled discharge capacity built into daily operations.' },
          { icon: Users, title: 'Driver Assistance', desc: 'Drivers assist passengers during transport.' },
          { icon: FileCheck, title: 'Credentialing-Ready Documentation', desc: 'Vendor documentation, insurance, and licensing prepared for hospital procurement review.' },
          { icon: ShieldCheck, title: 'HIPAA-Conscious Communication', desc: 'Professional handling of patient information through dispatch, driver, and destination handoff.' },
          { icon: Phone, title: 'Single Provider Relations Contact', desc: 'Direct provider relations point of contact for hospital partnership questions and escalation.' },
          { icon: MapPin, title: 'Greater Houston Coverage', desc: 'Verified coverage across Fort Bend and surrounding Greater Houston counties.' },
        ],
      }}
      transportRequest={{
        title: 'Request a Discharge Ride',
        description: 'Discharge planners and case managers can request same-day or scheduled discharge transportation through the Request Patient Transportation form or by calling dispatch directly.',
        cta: { label: 'Request Discharge Transportation', to: '/request-patient-transportation' },
      }}
      vendorRelationship={{
        title: 'Formalize a Hospital Partnership',
        description: 'Hospital procurement, contracting, and case management leadership can download MoveraCare’s vendor procurement packet or contact Provider Relations to begin credentialing and partnership onboarding.',
      }}
    />
  );
};

export default HospitalDischargePartners;
